import React, { useEffect, useState } from "react";
import api from "../../services/api";
import "../../App.css";

// Styles pour améliorer le responsive du tableau
const tableCellStyle = {
  wordBreak: "break-word",
  whiteSpace: "normal",
  verticalAlign: "middle"
};

export default function ListsUser() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [validationFilter, setValidationFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const usersPerPage = 10;

  // 👇 rôle de l'utilisateur connecté
  const [me, setMe] = useState(null);
  const [verifyingId, setVerifyingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchUsers();
    // récupérer le profil connecté pour savoir s'il est admin
    api.get("/me")
      .then(res => setMe(res.data?.user || res.data))
      .catch(() => setMe(null));
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, validationFilter, roleFilter]);

  const fetchUsers = () => {
    api.get("/admin/users")
      .then(res => setUsers(res.data))
      .catch(err => console.error("Erreur chargement utilisateurs :", err));
  };

  const isValidated = (u) =>
    u.is_verified === 1 || u.is_verified === "1" || u.is_verified === true;

  const filterUsers = () => {
    // Exclure les aspirants et les admins
    let filtered = users.filter((u) => u.role !== "aspirant" && u.role !== "admin");

    if (searchTerm) {
      filtered = filtered.filter((u) =>
        (u.name || "").toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (roleFilter !== "all") {
      filtered = filtered.filter((u) => u.role === roleFilter);
    }

    if (validationFilter !== "all") {
      const wantValidated = validationFilter === "validated";
      filtered = filtered.filter((u) => isValidated(u) === wantValidated);
    }

    setFilteredUsers(filtered);
    setCurrentPage(1);
  };

  const handleVerify = async (id) => {
    try {
      setVerifyingId(id);
      const response = await api.patch(`/admin/users/${id}/verify`);
      
      // Afficher le message de succès si disponible
      if (response.data?.message) {
        alert(response.data.message);
      } else {
        alert("✅ Utilisateur activé avec succès.");
      }
      
      // Recharger la liste des utilisateurs
      fetchUsers();
    } catch (err) {
      console.error("Erreur lors de la validation :", err);
      const errorMessage = err?.response?.data?.error || err?.message || "Erreur inconnue";
      alert(`❌ Échec de la validation : ${errorMessage}`);
    } finally {
      setVerifyingId(null);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'utilisateur "${name}" ?\n\nCette action est irréversible.`)) {
      return;
    }

    try {
      setDeletingId(id);
      const response = await api.delete(`/admin/users/${id}`);
      
      // Afficher le message de succès si disponible
      if (response.data?.message) {
        alert(response.data.message);
      } else {
        alert("✅ Utilisateur supprimé avec succès.");
      }
      
      // Recharger la liste des utilisateurs
      fetchUsers();
    } catch (err) {
      console.error("Erreur lors de la suppression :", err);
      const errorMessage = err?.response?.data?.error || err?.message || "Erreur inconnue";
      alert(`❌ Échec de la suppression : ${errorMessage}`);
    } finally {
      setDeletingId(null);
    }
  };

  // Pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const isViewerAdmin = (me?.role || "").toLowerCase() === "admin";

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between mb-4 gap-2">
        <h5 className="text-dark fw-bold mb-0" style={{ fontSize: "1.1rem" }}>Liste des Utilisateurs</h5>
        <span className="badge bg-info text-dark" style={{ fontSize: "0.875rem" }}>Total : {filteredUsers.length}</span>
      </div>

      <div className="row g-2 mb-4">
        <div className="col-12 col-md-4">
          <div className="input-group">
            <span className="input-group-text">
              <i className="bi bi-search"></i>
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Rechercher un utilisateur par son nom"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="col-6 col-md-2">
          <select
            className="form-select"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option value="all">Tous les rôles</option>
            <option value="coordinateur_district">Coordinateur District</option>
            <option value="coordinateur_region">Coordinateur Région</option>
            <option value="eglise">eglise</option>
          </select>
        </div>
        <div className="col-6 col-md-2">
          <select
            className="form-select"
            value={validationFilter}
            onChange={(e) => setValidationFilter(e.target.value)}
          >
            <option value="all">Tous les statuts</option>
            <option value="validated">Validés</option>
            <option value="not_validated">Non validés</option>
          </select>
        </div>
        <div className="col-12 col-md-4">
          <div className="d-flex justify-content-start justify-content-md-end">
            <span className="badge bg-secondary align-self-center">
              Page {currentPage} sur {totalPages}
            </span>
          </div>
        </div>
      </div>

      {filteredUsers.length === 0 ? (
        <div className="alert alert-info shadow-sm">
          <i className="bi bi-info-circle me-2"></i>
          {searchTerm || validationFilter !== "all" || roleFilter !== "all"
            ? "Aucun utilisateur ne correspond aux critères de recherche."
            : "Aucun utilisateur trouvé."}
        </div>
      ) : (
        <>
          {/* Version desktop : tableau */}
          <div className="table-responsive shadow-sm rounded overflow-hidden d-none d-md-block">
            <table className="table table-hover table-bordered table-sm align-middle mb-0" style={{ fontSize: "0.875rem" }}>
              <thead className="table-primary text-center">
                <tr>
                  <th style={{ minWidth: "150px", fontSize: "0.875rem" }}>Nom</th>
                  <th style={{ fontSize: "0.875rem" }}>Login</th>
                  <th style={{ fontSize: "0.875rem" }}>Téléphone</th>
                  <th style={{ fontSize: "0.875rem" }}>Rôle</th>
                  <th style={{ fontSize: "0.875rem" }}>District</th>
                  <th style={{ fontSize: "0.875rem" }}>Région</th>
                  <th style={{ fontSize: "0.875rem" }}>Validation</th>
                  {isViewerAdmin && <th style={{ fontSize: "0.875rem" }}>Actions</th>}
                </tr>
              </thead>
              <tbody style={{ fontSize: "0.875rem" }}>
                {currentUsers.map((u, index) => {
                  const validated = isValidated(u);
                  const globalIndex = indexOfFirstUser + index;

                  return (
                    <tr key={u.id}>
                      <td style={{ ...tableCellStyle, maxWidth: "200px" }}>{u.name}</td>
                      <td className="text-center" style={tableCellStyle}>{u.login || <span className="text-muted">—</span>}</td>
                      <td className="text-center" style={tableCellStyle}>{u.phone || <span className="text-muted">—</span>}</td>
                      <td className="text-center" style={tableCellStyle}>
                        <div className="d-flex flex-column align-items-center gap-1">
                          <span
                            className={`badge text-uppercase fw-semibold ${
                              u.role === "admin"
                                ? "bg-danger"
                                : u.role === "coordinateur_region"
                                ? "bg-warning text-dark"
                                : "bg-secondary"
                            }`}
                            style={{ fontSize: "0.75rem" }}
                          >
                            {u.role}
                          </span>
                          {u.eglise_name && (
                            <small className="text-muted" style={{ wordBreak: "break-word", textAlign: "center", fontSize: "0.75rem" }}>{u.eglise_name}</small>
                          )}
                        </div>
                      </td>
                      <td className="text-center" style={tableCellStyle}>{u.district_name || "-"}</td>
                      <td className="text-center" style={tableCellStyle}>{u.region_name || "-"}</td>

                      {/* ✅ Règle demandée :
                          - Si l'utilisateur connecté est ADMIN:
                              - afficher bouton "Activer" si NON validé
                              - afficher check si validé
                          - Sinon (viewer non admin):
                              - afficher check si validé, sinon tiret
                      */}
                      <td className="text-center">
                        {isViewerAdmin ? (
                          validated ? (
                            <i
                              className="bi bi-check-circle-fill text-success fs-5"
                              title="Déjà validé"
                            ></i>
                          ) : (
                            <button
                              className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                              onClick={() => handleVerify(u.id)}
                              disabled={verifyingId === u.id}
                              style={{ fontSize: "0.8rem" }}
                            >
                              {verifyingId === u.id ? (
                                <>
                                  <span className="spinner-border spinner-border-sm" />
                                  <span> Activation…</span>
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-exclamation-circle"></i> Activer
                                </>
                              )}
                            </button>
                          )
                        ) : validated ? (
                          <i
                            className="bi bi-check-circle-fill text-success fs-5"
                            title="Validé"
                          ></i>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      {isViewerAdmin && (
                        <td className="text-center">
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(u.id, u.name)}
                            disabled={deletingId === u.id}
                            title="Supprimer l'utilisateur"
                            style={{ fontSize: "0.8rem" }}
                          >
                            {deletingId === u.id ? (
                              <span className="spinner-border spinner-border-sm" />
                            ) : (
                              <i className="bi bi-trash"></i>
                            )}
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Version mobile : cartes */}
          <div className="d-md-none">
            {currentUsers.map((u) => {
              const validated = isValidated(u);
              return (
                <div key={u.id} className="card shadow-sm mb-3">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <h6 className="fw-semibold mb-1" style={{ wordBreak: "break-word", whiteSpace: "normal" }}>{u.name}</h6>
                        <div className="mb-2">
                          <small className="text-muted d-block">Login:</small>
                          {u.login ? (
                            <span className="badge bg-success">{u.login}</span>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </div>
                      </div>
                      <div>
                        <span
                          className={`badge text-uppercase fw-semibold ${
                            u.role === "admin"
                              ? "bg-danger"
                              : u.role === "coordinateur_region"
                              ? "bg-warning text-dark"
                              : "bg-secondary"
                          }`}
                        >
                          {u.role}
                        </span>
                      </div>
                    </div>

                    {u.eglise_name && (
                      <div className="mb-2">
                        <small className="text-muted d-block">Église:</small>
                        <span>{u.eglise_name}</span>
                      </div>
                    )}

                    <div className="row g-2 mb-2">
                      <div className="col-6">
                        <small className="text-muted d-block">Téléphone:</small>
                        <span>{u.phone || "—"}</span>
                      </div>
                      <div className="col-6">
                        <small className="text-muted d-block">District:</small>
                        <span>{u.district_name || "—"}</span>
                      </div>
                    </div>

                    <div className="mb-2">
                      <small className="text-muted d-block">Région:</small>
                      <span>{u.region_name || "—"}</span>
                    </div>

                    <div className="mt-3 pt-2 border-top">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <small className="text-muted">Validation:</small>
                        {isViewerAdmin ? (
                          validated ? (
                            <i
                              className="bi bi-check-circle-fill text-success fs-5"
                              title="Déjà validé"
                            ></i>
                          ) : (
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleVerify(u.id)}
                              disabled={verifyingId === u.id}
                            >
                              {verifyingId === u.id ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-1" />
                                  Activation…
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-exclamation-circle me-1"></i> Activer
                                </>
                              )}
                            </button>
                          )
                        ) : validated ? (
                          <i
                            className="bi bi-check-circle-fill text-success fs-5"
                            title="Validé"
                          ></i>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </div>
                      {isViewerAdmin && (
                        <div className="d-flex justify-content-end">
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(u.id, u.name)}
                            disabled={deletingId === u.id}
                            title="Supprimer l'utilisateur"
                          >
                            {deletingId === u.id ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" />
                                Suppression…
                              </>
                            ) : (
                              <>
                                <i className="bi bi-trash me-1"></i> Supprimer
                              </>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <nav className="mt-4">
              <ul className="pagination justify-content-center flex-wrap">
                <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <i className="bi bi-chevron-left"></i>
                    <span className="d-none d-sm-inline ms-1">Précédent</span>
                  </button>
                </li>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map((number) => {
                  if (
                    number === 1 ||
                    number === totalPages ||
                    (number >= currentPage - 1 && number <= currentPage + 1)
                  ) {
                    return (
                      <li
                        key={number}
                        className={`page-item ${currentPage === number ? "active" : ""}`}
                      >
                        <button className="page-link" onClick={() => paginate(number)}>
                          {number}
                        </button>
                      </li>
                    );
                  } else if (number === currentPage - 2 || number === currentPage + 2) {
                    return (
                      <li key={number} className="page-item disabled d-none d-sm-block">
                        <span className="page-link">...</span>
                      </li>
                    );
                  }
                  return null;
                })}

                <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    <span className="d-none d-sm-inline me-1">Suivant</span>
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </>
      )}
    </div>
  );
}
